import React, { useState } from 'react';
import { User, Bot, Copy, ThumbsUp, ThumbsDown, ExternalLink, CheckCircle, FileSearch, ChevronDown, ChevronUp } from 'lucide-react';
// @ts-ignore - react-markdown types
import ReactMarkdown from 'react-markdown';
import { Message, Source } from '../../types';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { getMessageFeedback, setMessageFeedback } from '../../services/analytics';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  isStreaming?: boolean;
  streamingMessageIds?: Set<string>;
}

interface MessageItemProps {
  message: Message;
  isStreaming?: boolean;
}

const TypingIndicator: React.FC = () => {
  return (
    <span className="inline-flex items-center ml-1.5 align-middle">
      <span className="typing-dot" />
      <span className="typing-dot" style={{ animationDelay: '0.2s' }} />
      <span className="typing-dot" style={{ animationDelay: '0.4s' }} />
    </span>
  );
};

const MessageItem: React.FC<MessageItemProps> = ({ message, isStreaming = false }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  const showTypingIndicator = isStreaming && (isAssistant || isSystem);

  // Per-message UI state
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [isCitationsModalOpen, setIsCitationsModalOpen] = useState<boolean>(false);

  // Initialize feedback state from local storage if previously set
  React.useEffect(() => {
    if (message.id) {
      setFeedback(getMessageFeedback(message.id));
    }
  }, [message.id]);


  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // TODO: Show toast notification
  };

  const handleThumbs = async (value: 'up' | 'down') => {
    if (!message.id) return;
    const newValue = feedback === value ? null : value; // clicking again clears
    setFeedback(newValue);
    setMessageFeedback(message.id, newValue).catch(() => {});
  };

  return (
    <div className={`flex space-x-2 sm:space-x-3 items-start ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
        isUser 
          ? 'bg-primary-600 text-white' 
          : isSystem 
            ? 'bg-gray-500 text-white'
            : 'bg-gray-100 text-gray-600'
      }`}>
        {isUser ? (
          <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        ) : isSystem ? (
          <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        ) : (
          <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        )}
      </div>

      {/* Message content */}
      <div className={`flex-1 max-w-[85%] sm:max-w-3xl ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block max-w-full rounded-lg break-words ${
          isUser 
            ? 'bg-primary-50 text-primary-900 px-2 py-1' 
            : isSystem
              ? 'bg-gray-100 text-gray-800 p-2.5 sm:p-3'
              : 'bg-gray-50 text-gray-900 p-2.5 sm:p-3'
        }`}>
          {/* Message content - render markdown for assistant messages */}
          {isUser ? (
            <div className="text-sm whitespace-pre-wrap break-words">
              {message.content}
            </div>
          ) : isSystem ? (
            <div className="text-sm whitespace-pre-wrap break-words">
              <span className="inline-flex items-center">
                <span>{message.content}</span>
                {showTypingIndicator && (
                  <span className="inline-flex items-center ml-1.5">
                    <TypingIndicator />
                  </span>
                )}
              </span>
            </div>
          ) : (
            <div className="prose prose-sm max-w-full break-words">
              <ReactMarkdown
                components={{
                  p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                  h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-base font-bold mb-2" {...props} />,
                  code: ({node, ...props}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props} />,
                  pre: ({node, ...props}) => <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto" {...props} />,
                }}
              >
                {message.content}
              </ReactMarkdown>
              {showTypingIndicator && (
                <span className="inline-flex items-center ml-1.5 mt-1">
                  <TypingIndicator />
                </span>
              )}
            </div>
          )}
        </div>

        {/* Citations Modal - Shows all citations when clicking the Sources button */}
        {message.citations && message.citations.length > 0 && (
          <CitationsModal
            citations={message.citations}
            isOpen={isCitationsModalOpen}
            onClose={() => setIsCitationsModalOpen(false)}
          />
        )}

        {/* Message actions */}
        <div className={`flex items-center flex-wrap gap-1 sm:gap-2 mt-1.5 sm:mt-2 ${isUser ? 'justify-end' : ''}`}>
          {!isUser && (
            <>
              <Button
                variant="ghost"
                size="sm"
                icon={Copy}
                onClick={() => handleCopy(message.content)}
                className="p-1.5 sm:p-2"
              />

              {message.citations && message.citations.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={FileSearch}
                  onClick={() => setIsCitationsModalOpen(true)}
                  title="View Citations"
                  aria-label="View citations"
                  className="p-1.5 sm:p-2"
                />
              )}
              
              <Button
                variant={feedback === 'up' ? 'secondary' : 'ghost'}
                size="sm"
                icon={ThumbsUp}
                onClick={() => handleThumbs('up')}
                className="p-1.5 sm:p-2"
              />
              
              <Button
                variant={feedback === 'down' ? 'secondary' : 'ghost'}
                size="sm"
                icon={ThumbsDown}
                onClick={() => handleThumbs('down')}
                className="p-1.5 sm:p-2"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface CitationsModalProps {
  citations: Source[];
  isOpen: boolean;
  onClose: () => void;
}

const CitationsModal: React.FC<CitationsModalProps> = ({ citations, isOpen, onClose }) => {
  const [expandedCitations, setExpandedCitations] = useState<Set<number>>(new Set());
  
  // Expand all citations by default when modal opens
  React.useEffect(() => {
    if (isOpen && citations && citations.length > 0) {
      setExpandedCitations(new Set(citations.map((_, index) => index)));
    }
  }, [isOpen, citations]);

  const toggleCitation = (index: number) => {
    setExpandedCitations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (!citations || citations.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Citations (${citations.length})`}
      maxWidth="2xl"
    >
      <div className="max-h-[70vh] overflow-y-auto space-y-4 pr-2">
        {citations.map((citation, index) => {
          const hasLink = Boolean(citation.doc_id);
          const href = hasLink
            ? `/document/${citation.doc_id}${citation.page ? `?page=${citation.page}` : ''}`
            : undefined;
          const fullContent = citation.content || citation.snippet || 'No content available';
          const score = typeof citation.score === 'number' ? citation.score : null;
          const isExpanded = expandedCitations.has(index);

          return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              {/* Citation Header - Clickable to toggle */}
              <div 
                className="flex items-start justify-between p-4 cursor-pointer"
                onClick={() => toggleCitation(index)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                      Citation #{index + 1}
                    </span>
                    {score !== null && (
                      <span className="text-xs font-semibold text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded">
                        Score: {Math.round(score * 100)}%
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 truncate">
                    {citation.title || citation.doc_id || 'Document'}
                  </h4>
                  <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                    {typeof citation.page === 'number' && citation.page > 0 && (
                      <span>Page {citation.page}</span>
                    )}
                    {hasLink && href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3 w-3" />
                        View Document
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  {score !== null && (
                    <div className="flex-shrink-0 text-right">
                      <div className="text-xs text-gray-500 mb-1">Relevance</div>
                      <div className="text-lg font-semibold text-blue-600 font-mono">
                        {Math.round(score * 100)}%
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {score.toFixed(4)}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCitation(index);
                    }}
                    className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                    aria-label={isExpanded ? 'Collapse citation' : 'Expand citation'}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Full Chunk Content - Collapsible */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-300 pt-3">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    Complete Chunk Text
                  </h5>
                  <div className="bg-white border border-gray-200 rounded p-3 max-h-64 overflow-y-auto">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                      {fullContent}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

const LoadingIndicator: React.FC = () => {
  return (
    <div className="flex space-x-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
        <Bot className="h-4 w-4 text-gray-600" />
      </div>
      <div className="flex-1">
        <div className="inline-block p-3 bg-gray-50 rounded-lg">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageList: React.FC<MessageListProps> = ({ messages, isLoading, isStreaming = false, streamingMessageIds = new Set() }) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, isStreaming, streamingMessageIds]);

  // Find the last assistant message to show typing indicator (for chat messages)
  const lastAssistantMessageIndex = messages.length - 1;
  const lastMessage = messages[lastAssistantMessageIndex];
  const isLastMessageAssistant = lastMessage?.role === 'assistant';

  return (
    <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
      <style>{`
        @keyframes typing-fade {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }
        .typing-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #9ca3af;
          margin: 0 2px;
          animation: typing-fade 1.4s infinite ease-in-out;
          vertical-align: middle;
        }
      `}</style>
      {messages.map((message, index) => {
        // Check if this message is in the streaming set (for uploads) or is the last assistant message (for chat)
        const isMessageStreaming = streamingMessageIds.has(message.id) || 
          (isStreaming && isLastMessageAssistant && index === lastAssistantMessageIndex && message.role === 'assistant');
        return (
          <div key={message.id} className="group">
            <MessageItem message={message} isStreaming={isMessageStreaming} />
          </div>
        );
      })}
      
      {isLoading && !isStreaming && streamingMessageIds.size === 0 && (
        <div className="group">
          <LoadingIndicator />
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
